import { ChangeEvent, ReactElement, useCallback, useMemo } from 'react'
import { Divider } from '@mui/material'
import { PopoverPanel, TwoColumnGrid } from '@ui2/structure'
import { CheckboxLabeled, SwitchLabeled } from '@ui/actions'
import { useModelStore } from '@editorStores'
import { elementTypeToLabelSingular } from 'src/domain/constants/labels'

const TRANSPARENCY_VISIBILITY_ELEMENT_TYPES: ElementTypes[] = [
  'outer_walls',
  'inner_walls',
  'vertical_slabs',
  'vertical_roof_slabs',
  'slabs',
  'roof_slabs',
  'beams',
  'columns',
  'purlins',
  'rips',
  'lintels',
]

const transparencyVisibilityOptions = TRANSPARENCY_VISIBILITY_ELEMENT_TYPES.map(value => ({
  value,
  label: elementTypeToLabelSingular[value] || value,
}))

interface Props {
  anchorEl: HTMLElement | null
  onClose: () => void
}

/**
 * Settings popover for the Modell storey controls (opened from the header gear icon). Holds the
 * "Transparent" toggle with its per-element-type transparency selection, and the "Sichtbarkeit
 * koppeln" switches, mirroring the IFC column's settings popover.
 */
const ModelSettingsPopover = ({ anchorEl, onClose }: Props): ReactElement => {
  const translucent = useModelStore(state => state.translucent)
  const setTranslucent = useModelStore(state => state.setTranslucent)
  const { visibilityByType: transparencyVisibilityByType } = useModelStore(
    state => state.transparencyVisibilityModel,
  )
  const setTransparencyVisibilityForTypes = useModelStore(
    state => state.setTransparencyVisibilityForTypes,
  )
  const positionVisibilityFollowsWallVisibility = useModelStore(
    state => state.positionVisibilityFollowsWallVisibility,
  )
  const setPositionVisibilityFollowsWallVisibility = useModelStore(
    state => state.setPositionVisibilityFollowsWallVisibility,
  )
  const slabPositionVisibilityFollowsSlabVisibility = useModelStore(
    state => state.slabPositionVisibilityFollowsSlabVisibility,
  )
  const setSlabPositionVisibilityFollowsSlabVisibility = useModelStore(
    state => state.setSlabPositionVisibilityFollowsSlabVisibility,
  )
  const roofSlabPositionVisibilityFollowsRoofSlabVisibility = useModelStore(
    state => state.roofSlabPositionVisibilityFollowsRoofSlabVisibility,
  )
  const setRoofSlabPositionVisibilityFollowsRoofSlabVisibility = useModelStore(
    state => state.setRoofSlabPositionVisibilityFollowsRoofSlabVisibility,
  )

  const selectedTransparencyTypes = useMemo(
    () => TRANSPARENCY_VISIBILITY_ELEMENT_TYPES.filter(type => transparencyVisibilityByType[type]),
    [transparencyVisibilityByType],
  )

  const handleTransparencyCheckboxChange = useCallback(
    (type: ElementTypes) => (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target
      if (checked) {
        setTransparencyVisibilityForTypes([...new Set([...selectedTransparencyTypes, type])])
      } else {
        setTransparencyVisibilityForTypes(
          selectedTransparencyTypes.filter(selectedType => selectedType !== type),
        )
      }
    },
    [selectedTransparencyTypes, setTransparencyVisibilityForTypes],
  )

  return (
    <PopoverPanel
      anchorEl={anchorEl}
      onClose={onClose}
      title="Modell"
      dataCy="model-settings-popover"
    >
      <SwitchLabeled
        checked={translucent}
        onChange={event => setTranslucent(event.target.checked)}
        label="Transparent"
        sx={{ p: '4px' }}
      />
      {translucent && (
        <TwoColumnGrid>
          {transparencyVisibilityOptions.map(({ value, label }) => (
            <CheckboxLabeled
              key={value}
              checked={selectedTransparencyTypes.includes(value)}
              onChange={handleTransparencyCheckboxChange(value)}
              label={label}
            />
          ))}
        </TwoColumnGrid>
      )}

      <Divider>Sichtbarkeit koppeln</Divider>
      <SwitchLabeled
        checked={positionVisibilityFollowsWallVisibility}
        onChange={() =>
          setPositionVisibilityFollowsWallVisibility(!positionVisibilityFollowsWallVisibility)
        }
        label="Sichtbarkeit von Fenstersturz und Rippe an Wand koppeln"
        sx={{ p: '4px' }}
        data-cy="switch-position-visibility-follows-wall-visibility"
      />
      <SwitchLabeled
        checked={slabPositionVisibilityFollowsSlabVisibility}
        onChange={() =>
          setSlabPositionVisibilityFollowsSlabVisibility(
            !slabPositionVisibilityFollowsSlabVisibility,
          )
        }
        label="Sichtbarkeit von Deckenposition an Deckenfläche  koppeln"
        sx={{ p: '4px' }}
        data-cy="switch-slab-position-visibility-follows-slab-visibility"
      />
      <SwitchLabeled
        checked={roofSlabPositionVisibilityFollowsRoofSlabVisibility}
        onChange={() =>
          setRoofSlabPositionVisibilityFollowsRoofSlabVisibility(
            !roofSlabPositionVisibilityFollowsRoofSlabVisibility,
          )
        }
        label="Sichtbarkeit von Dachposition an Dachfläche koppeln"
        sx={{ p: '4px' }}
        data-cy="switch-roof-slab-position-visibility-follows-roof-slab-visibility"
      />
    </PopoverPanel>
  )
}

export default ModelSettingsPopover
